import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { IonContent, IonPage, isPlatform, useIonAlert } from '@ionic/react'
import { useT } from '@transifex/react'
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { customerBackend, customerFrontend } from '../apiConfig'
import DummyHeader from '../components/DummyHeader'
import useBarcodeScanner from '../hooks/useBarcodeScanner'
import BLE from '../plugins/BLEPlugin'
import IBoxenBLE from '../plugins/IBoxenBLEPlugin'
import { logEvent } from '../plugins/Intercom'
import sendToIframe from '../sendToIframe'
import { putLogEvents } from '../services/aws'
import { selectCustomer, selectSession } from '../store'
import translationMappings from '../transifex/translationMappings'

const iframeTitle = 'store-pickup'

const errorsToTriggerOpenSettings = ['missing_permission:ACCESS_FINE_LOCATION', 'missing_permission:ACCESS_BLUETOOTH', 'missing_permission:BLUETOOTH_SCAN']

let disconnectTimeoutId: NodeJS.Timeout

export default function StorePickup() {
  const { authToken, locale } = useSelector(selectSession)
  const customer = useSelector(selectCustomer)
  const t = useT()
  const [presentAlert] = useIonAlert()
  const [startScan, , barcode] = useBarcodeScanner()
  const [hasPendingGattConnection, setHasPendingGattConnection] = useState(false)

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      try {
        const { name, route, payload } = JSON.parse(event.data)

        if (name === 'loaded' && authToken) {
          sendToIframe(
            {
              name: 'customer',
              authToken,
              customerId: customer.customer._id,
              backendUrl: customerBackend,
              locale: locale || 'sv',
            },
            iframeTitle
          )
        }
        if (name === 'openPeripheral') {
          const shouldKeepGattConnection = isPlatform('android') && Capacitor.isNativePlatform() && (route.includes('scan') || route.includes('test'))
          if (shouldKeepGattConnection) {
            clearTimeout(disconnectTimeoutId)
            IBoxenBLE.sendSequencePayload({ payload })
              .then(({ logEntries }) => {
                sendToIframe({ name: 'lockOpened', payload }, iframeTitle)
                setHasPendingGattConnection(true)

                logEvent('lock_opened', { publicKey: payload.substring(0, 64) })
                putLogEvents('customer', customer.customer._id, logEntries, 'execute_payloads', true)

                disconnectTimeoutId = setTimeout(() => {
                  IBoxenBLE.cancel().then(() => {
                    setHasPendingGattConnection(false)
                  })
                }, 30000)
              })
              .catch((err) => {
                if (err.message === 'disconnected') return
                sendToIframe({ name: 'lockNotOpened', error: { message: err.message }, payload }, iframeTitle)

                logEvent('error', { message: err.message })
                putLogEvents('customer', customer.customer._id, err.logEntries, 'execute_payloads', false, err.message)
              })
          } else {
            const publicKey = payload.slice(0, 64)
            const payloads = payload.slice(64).split('.')

            Haptics.impact({ style: ImpactStyle.Light })

            BLE.executePayloads({
              public_key: publicKey,
              peripheral: publicKey.slice(0, 8),
              payloads,
            })
              .then(({ logEntries }) => {
                sendToIframe({ name: 'lockOpened', payload }, iframeTitle)
                Haptics.impact({ style: ImpactStyle.Light })

                logEvent('lock_opened', { publicKey: payloads[0] })
                putLogEvents('customer', customer.customer._id, logEntries, 'execute_payloads', true)
              })
              .catch((err) => {
                if (errorsToTriggerOpenSettings.includes(err.message)) {
                  const buttons = [
                    {
                      text: t('Settings'),
                      handler: () => {
                        NativeSettings.open({
                          optionAndroid: AndroidSettings.ApplicationDetails,
                          optionIOS: IOSSettings.App,
                        })
                      },
                    },
                    t('Cancel'),
                  ]

                  presentAlert({
                    header: t('Error'),
                    message: t(translationMappings[err.message] || err.message),
                    buttons,
                  })
                }
                sendToIframe({ name: 'lockNotOpened', error: { message: err.message }, payload }, iframeTitle)
                logEvent('error', { message: err.message })
                putLogEvents('customer', customer.customer._id, err.logEntries, 'execute_payloads', false, err.message)
              })
          }
        } else if (name === 'peripherals') {
          BLE.getQlocxPeripherals()
            .then(({ peripherals, logEntries }) => {
              sendToIframe({ name: 'peripherals', peripherals }, iframeTitle)
              putLogEvents('customer', customer.customer._id, logEntries, 'get_qlocx_peripherals', true)
            })
            .catch((err) => {
              if (errorsToTriggerOpenSettings.includes(err.message)) {
                const buttons = [
                  {
                    text: t('Settings'),
                    handler: () => {
                      NativeSettings.open({
                        optionAndroid: AndroidSettings.ApplicationDetails,
                        optionIOS: IOSSettings.App,
                      })
                    },
                  },
                  t('Cancel'),
                ]

                presentAlert({
                  header: t('Error'),
                  message: t(translationMappings[err.message] || err.message),
                  buttons,
                })
              }
              sendToIframe({ name: 'peripherals', error: { message: err.message } }, iframeTitle)
              logEvent('error', { message: err.message })
              putLogEvents('customer', customer.customer._id, err.logEntries, 'execute_payloads', false, err.message)
            })
        } else if (name === 'openBarcodeScanner') {
          startScan()
        } else if (isPlatform('android') && name === 'disconnectGatt') {
          IBoxenBLE.cancel().then(() => {
            setHasPendingGattConnection(false)
          })
        }
      } catch {
        // ignore
      }
    }
    const onAppPause = () => {
      if (hasPendingGattConnection) {
        IBoxenBLE.cancel().then(() => {
          setHasPendingGattConnection(false)
        })
      }
    }

    window.addEventListener('message', handler)
    const pauseListener = App.addListener('pause', onAppPause)

    return () => {
      window.removeEventListener('message', handler)
      pauseListener.then((l) => l.remove())
      clearTimeout(disconnectTimeoutId)
    }
  }, [authToken, hasPendingGattConnection])

  useEffect(() => {
    if (barcode) {
      sendToIframe({ name: 'barcodeScanned', barcode }, iframeTitle)
    }
  }, [barcode])

  // TODO: PAUSE HANDLER FOR DISCONNECTING GATT AUTOMATICALLLY
  const iframeSrc = useMemo(() => {
    if (!customer) {
      return ''
    }

    if (customer?.customer.hasStorePickupBooking) {
      return `${customerFrontend}?${iframeTitle}/client`
    }

    return `${customerFrontend}?${iframeTitle}/staff`
  }, [customer])

  return (
    <IonPage>
      <DummyHeader />
      <IonContent>
        <iframe title={iframeTitle} src={iframeSrc} width="100%" height="100%" allowFullScreen />
      </IonContent>
    </IonPage>
  )
}
